import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import { ApiError } from '../../lib/api';
import { Button, Dialog, Field, Input, Toast } from '../../ui';
import { FormError } from '../auth/AuthLayout';
import { emailSchema } from '../auth/schemas';
import { useSession } from '../auth/session';
import { useCaptcha } from '../auth/Turnstile';
import { Section } from '../dashboard/cards';

type Errors = { email?: string; confirm?: string; password?: string; form?: string };

/**
 * « Adresse e-mail » (P17): the sign-in address and a dialog to change it. The change goes through the Worker (no
 * confirmation email can be sent): it re-checks the password. A guest has no address, so no section.
 */
export function EmailSection() {
  const { t } = useTranslation();
  const session = useSession();
  const [open, setOpen] = useState(false);
  const [changed, setChanged] = useState<string | null>(null);
  if (session.status !== 'signed-in' || session.user.isAnonymous || !session.user.email) return null;
  const current = session.user.email;
  return (
    <Section title={t('settings.email.title')}>
      <div className="flex max-w-sm flex-col gap-1">
        <div className="flex flex-wrap items-center gap-3">
          <p className="min-w-0 flex-1 truncate font-sans text-sm text-fg">{current}</p>
          <Button onClick={() => setOpen(true)}>{t('settings.email.change')}</Button>
        </div>
        <p className="font-sans text-xs text-fg-muted">{t('settings.email.hint')}</p>
      </div>
      {open && <ChangeEmailDialog current={current} onChanged={setChanged} onClose={() => setOpen(false)} />}
      {changed && <Toast message={t('settings.email.changed', { email: changed })} onDone={() => setChanged(null)} />}
    </Section>
  );
}

function ChangeEmailDialog({ current, onChanged, onClose }: { current: string; onChanged: (email: string) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const captcha = useCaptcha();
  const [email, setEmail] = useState('');
  const [confirm, setConfirm] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const refuse = (next: Errors) => {
    setErrors(next);
    (next.email ? emailRef : next.confirm ? confirmRef : next.password ? passwordRef : null)?.current?.focus();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const parsed = emailSchema.safeParse(email);
    const typed = parsed.success ? parsed.data : '';
    const next: Errors = {
      ...(!parsed.success && { email: t('auth.invalidEmail') }),
      ...(parsed.success && typed.toLowerCase() === current.toLowerCase() && { email: t('settings.email.same') }),
      ...(parsed.success && confirm.trim().toLowerCase() !== typed.toLowerCase() && { confirm: t('settings.email.mismatch') }),
      ...(!password && { password: t('settings.email.passwordRequired') }),
    };
    if (Object.keys(next).length > 0) return refuse(next);
    setErrors({});
    setBusy(true);
    try {
      const saved = await api.changeEmail({ email: typed, password, ...(captcha.token ? { captchaToken: captcha.token } : {}) });
      onChanged(saved.email);
      onClose();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : null;
      captcha.reset(); // a Turnstile token is single-use
      setBusy(false);
      if (code === 'invalid_credentials') {
        setPassword(''); // never kept on screen after a refusal
        return refuse({ password: t('settings.email.wrongPassword') });
      }
      if (code === 'email_taken') return refuse({ email: t('settings.email.taken') });
      if (code === 'unchanged') return refuse({ email: t('settings.email.same') });
      if (code === 'invalid') return refuse({ email: t('auth.invalidEmail') });
      setErrors({ form: t(code === 'captcha_failed' ? 'settings.email.captcha' : code === 'limit' ? 'auth.tooManyAttempts' : 'auth.genericError') });
    }
  };

  return (
    <Dialog title={t('settings.email.dialogTitle')} onClose={onClose}>
      <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <Field label={t('settings.email.new')} error={errors.email}>
          <Input ref={emailRef} type="email" autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label={t('settings.email.confirm')} error={errors.confirm}>
          <Input ref={confirmRef} type="email" autoComplete="off" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        <Field label={t('settings.email.password')} error={errors.password} hint={t('settings.email.passwordHint')}>
          <Input ref={passwordRef} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {captcha.widget}
        <FormError message={errors.form ?? null} />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t('dashboard.cancel')}</Button>
          <Button variant="primary" type="submit" loading={busy} disabled={!captcha.ready}>
            {busy ? t('settings.email.saving') : t('settings.email.submit')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
